import { useState } from "react";
import s from "./Liability.module.css";
import { Page } from "../../layout/AppShell";
import { useLiabilityCurve, useLiabilityRegister } from "../../api/queries";
import {
  byEnvelope,
  costOfWaiting,
  coverage,
  totalsAreRepresentative,
  type LiabilityCurve,
  type LiabilityRegister,
  type LiabilityRow,
  type Scenario,
} from "../../domain/liability";
import { Card, EmptyState, ErrorState, Segmented, Skeleton, Table } from "../../design/components";
import { formatDate } from "../../lib/format";
import { locale, t, tn } from "../../i18n";

type View = "curve" | "register";

const YEARS = [0, 1, 2].map((n) => new Date().getUTCFullYear() + n);

/**
 * What deferring maintenance costs.
 *
 * The screen exists to win a budget argument, and the number that wins it is
 * the cost of waiting a year — not the size of the backlog, which every
 * asset-management system already prints.
 *
 * THE DISCIPLINE THIS PAGE ENFORCES is that the money never appears without
 * its coverage. A candidate that cannot be priced is excluded from every
 * total, and a total that quietly omits a third of the register is worse than
 * no total. Someone will screenshot the big figure, so the coverage sits in
 * the same block rather than in a footnote beneath it.
 */
export function LiabilityPage() {
  const [view, setView] = useState<View>("curve");
  const [year, setYear] = useState(YEARS[1]);
  const curve = useLiabilityCurve(year);
  const register = useLiabilityRegister();

  return (
    <Page
      eyebrow="Deep thinking"
      title="What deferring maintenance costs"
      actions={
        <Segmented
          value={view}
          onChange={setView}
          ariaLabel="View"
          options={[
            { value: "curve", label: "Budget curve" },
            { value: "register", label: "Register" },
          ]}
        />
      }
    >
      {view === "curve" && (
        <>
          <Card
            title="Fiscal year"
            sub="Each envelope is a budget you might be given. What changes is which candidates slip."
          >
            <Segmented
              value={String(year)}
              onChange={(v) => setYear(Number(v))}
              ariaLabel="Fiscal year"
              options={YEARS.map((y) => ({ value: String(y), label: String(y) }))}
            />
          </Card>

          {curve.isPending && <Skeleton rows={6} />}
          {curve.isError && <ErrorState error={curve.error} retry={() => curve.refetch()} />}
          {curve.data && <Curve curve={curve.data} />}
        </>
      )}

      {view === "register" && (
        <>
          {register.isPending && <Skeleton rows={8} />}
          {register.isError && <ErrorState error={register.error} retry={() => register.refetch()} />}
          {register.data && <Register register={register.data} />}
        </>
      )}
    </Page>
  );
}

function Coverage({ c }: { c: LiabilityCurve | LiabilityRegister }) {
  const complete = totalsAreRepresentative(c);
  const f = coverage(c);
  return (
    <div className={`${s.coverage} ${complete ? s.complete : s.partial}`} data-testid="coverage" role="note">
      <div className={s.coverageHead}>{complete ? t("Coverage") : t("These totals are incomplete")}</div>
      <div className={s.coverageLine}>
        {"coverageNote" in c && c.coverageNote
          ? c.coverageNote
          : "note" in c && c.note
            ? c.note
            : t("{priced} of {candidates} candidates carry a cost.", { priced: c.priced, candidates: c.candidates })}
      </div>
      {f != null && !complete && (
        <div className={s.moneyLabel}>
          {t(
            "Every figure below is the sum of the {priced} that could be priced. The other {notPriced} are not zero — nobody has priced them.",
            { priced: c.priced, notPriced: c.notPriced },
          )}
        </div>
      )}
    </div>
  );
}

function Curve({ curve }: { curve: LiabilityCurve }) {
  if (!curve.available) {
    return <EmptyState>No liability curve for this year — no candidates have been forecast yet.</EmptyState>;
  }
  const scenarios = byEnvelope(curve.scenarios);
  return (
    <>
      <Coverage c={curve} />
      <Card
        title={t("Budget envelopes, {year}", { year: curve.fiscalYear })}
        sub="Read it worst first: the tighter the budget, the more slips and the more waiting costs."
      >
        {scenarios.length === 0 ? (
          <EmptyState>No envelopes are configured for this site.</EmptyState>
        ) : (
          <div className={s.scenarios}>
            {scenarios.map((x) => (
              <ScenarioCard key={x.envelope} scenario={x} currency={curve.currency} />
            ))}
          </div>
        )}
      </Card>

      {curve.notPricedDetail.length > 0 && (
        <Card
          title="Excluded from every figure above"
          sub="Named rather than dropped: an excluded candidate is a gap in the number, not an absence of work."
        >
          <Table>
            <thead>
              <tr>
                <th>{t("Candidate")}</th>
                <th>{t("Why it carries no cost")}</th>
              </tr>
            </thead>
            <tbody>
              {curve.notPricedDetail.map((u) => (
                <tr key={u.title}>
                  <td>{u.title}</td>
                  <td className={s.basis}>{u.whyNot}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}
    </>
  );
}

const money = (n: number, currency: string) =>
  `${currency ? currency + " " : ""}${Math.round(n).toLocaleString(locale())}`;

function ScenarioCard({ scenario, currency }: { scenario: Scenario; currency: string }) {
  const waiting = costOfWaiting(scenario);
  return (
    <div className={s.scenario}>
      <div className={s.envelope}>{t("At {amount}", { amount: money(scenario.envelope, currency) })}</div>

      {/* The sentence that wins the argument — or an honest absence of one.
          `costOfWaiting` returns null rather than zero when nothing could be
          escalated, because "waiting costs 0" reads as "waiting is free". */}
      {waiting == null ? (
        <div className={`${s.money} ${s.moneyNone}`}>
          {scenario.deferred === 0 ? t("Nothing slips") : t("Cost of waiting not computable")}
        </div>
      ) : (
        <div className={s.money}>+{money(waiting, currency)}</div>
      )}
      <div className={s.moneyLabel}>
        {waiting == null
          ? scenario.deferred === 0
            ? t("This envelope funds every priced candidate.")
            : t("{n} slip, but their escalation could not be computed.", { n: scenario.deferred })
          : tn(
              scenario.deferred,
              "is what waiting one year costs on the {n} candidate this envelope cannot fund.",
              "is what waiting one year costs on the {n} candidates this envelope cannot fund.",
            )}
      </div>

      <div className={s.split}>
        <div className={s.splitCell}>
          <span>{t("Funded")}</span>
          <b>
            {scenario.funded} · {money(scenario.fundedCost, currency)}
          </b>
        </div>
        <div className={s.splitCell}>
          <span>{t("Deferred")}</span>
          <b>
            {scenario.deferred} · {money(scenario.deferredCostNow, currency)}
          </b>
        </div>
      </div>

      {scenario.deferredDetail.length > 0 && (
        <div className={s.deferredList}>
          {scenario.deferredDetail.map((d) => (
            <div className={s.deferredItem} key={d.title}>
              <span>{d.title}</span>
              {/* Closure hours are frequently unknowable, and unknown is not
                  zero: a candidate naming neither zone nor asset has nothing
                  to attribute a closure to. */}
              <span className={d.consequence.known ? undefined : s.unknownConsequence}>
                {d.consequence.known
                  ? t("{n} closure h/yr", { n: d.consequence.closureHoursPerYear ?? 0 })
                  : t("closure hours unknown")}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Register({ register }: { register: LiabilityRegister }) {
  return (
    <>
      <Coverage c={register} />
      <Card
        title="The register"
        sub="Every priced figure carries the rate, the date it was priced, the quantity and the deferral factor — so an input can be disputed instead of the conclusion dismissed."
      >
        <Table>
          <thead>
            <tr>
              <th>{t("Candidate")}</th>
              <th>{t("Forecast")}</th>
              <th className={s.num}>{t("Cost now")}</th>
              <th className={s.num}>{t("If deferred")}</th>
              <th>{t("Basis")}</th>
            </tr>
          </thead>
          <tbody>
            {register.register.map((r) => (
              <Row key={r.id} row={r} />
            ))}
          </tbody>
        </Table>
      </Card>
    </>
  );
}

function Row({ row }: { row: LiabilityRow }) {
  if (!row.priced) {
    return (
      <tr className={s.unpricedRow}>
        <td>{row.title}</td>
        <td>{formatDate(row.forecastFor)}</td>
        {/* Not a dash meaning zero — a word meaning nobody has priced it. */}
        <td className={s.num} colSpan={2}>
          {t("not priced")}
        </td>
        <td className={s.basis}>{row.whyNot}</td>
      </tr>
    );
  }
  const cur = row.currency ?? "";
  return (
    <tr>
      <td>{row.title}</td>
      <td>{formatDate(row.forecastFor)}</td>
      <td className={s.num}>{row.costNow != null ? money(row.costNow, cur) : "—"}</td>
      <td className={s.num}>
        {row.costDeferred != null ? money(row.costDeferred, cur) : "—"}
        {row.escalation ? <div className={s.basis}>+{money(row.escalation, cur)}</div> : null}
      </td>
      <td className={s.basis}>
        {row.basis?.quantity && row.basis?.rate
          ? t("{quantity} {unit} at {rate}, priced {date} ({source}). ", {
              quantity: row.basis.quantity,
              unit: row.basis.unit ?? "",
              rate: row.basis.rate,
              date: formatDate(row.basis.pricedOn),
              source: row.basis.rateSource ?? t("source not stated"),
            })
          : ""}
        {row.basis?.deferralBasis}
      </td>
    </tr>
  );
}
